import baseApi from "../../../Lib/api/baseApi";
import endpoints from "../../../Lib/api/endpoints";
import { uniqueAreas } from "../utils/areas";

const getEntityId = (entity, fallback) =>
  entity?.id || entity?._id || entity?.zoneId || entity?.areaId || fallback;

const normalizeArea = (area, index = 0) => ({
  ...area,
  id: getEntityId(area, `area_${index}`),
});

const normalizeZone = (zone, index = 0) => ({
  ...zone,
  id: getEntityId(zone, `zone_${index}`),
  areas: uniqueAreas((zone?.areas || []).map(normalizeArea)),
});

const normalizeZonePrice = (zonePrice, index = 0) => ({
  ...zonePrice,
  id: getEntityId(zonePrice, `zone_price_${index}`),
});

export const ZoneApi = baseApi.injectEndpoints({
  tagTypes: ["Zone", "ZonePrice"],
  endpoints: (builder) => ({
    getAllZones: builder.query({
      query: (data) => ({
        ...endpoints.getAllZones(data),
      }),
      transformResponse: (response) => ({
        data: (response?.data?.data || []).map(normalizeZone),
        page: response?.data?.page || 0,
        total: response?.data?.total || 0,
        limit: response?.data?.limit || 0,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((zone) => ({ type: "Zone", id: zone.id })),
              { type: "Zone", id: "All" },
            ]
          : [{ type: "Zone", id: "All" }],
    }),
    getZoneById: builder.query({
      query: (id) => ({
        ...endpoints.getZoneById(id),
      }),
      transformResponse: (response) => ({
        ...response,
        data: response?.data ? normalizeZone(response.data) : response?.data,
      }),
      providesTags: (result, error, id) => [{ type: "Zone", id }],
    }),
    createZone: builder.mutation({
      query: (body) => ({
        ...endpoints.createZone,
        data: body,
      }),
      invalidatesTags: [{ type: "Zone", id: "All" }],
    }),
    updateZone: builder.mutation({
      query: ({ id, body }) => ({
        ...endpoints.updateZone(id),
        data: body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Zone", id: "All" },
        { type: "Zone", id },
      ],
    }),
    deleteZone: builder.mutation({
      query: (id) => ({
        ...endpoints.deleteZone(id),
      }),
      invalidatesTags: [{ type: "Zone", id: "All" }],
    }),
    activateZone: builder.mutation({
      query: (id) => ({
        ...endpoints.activateZone(id),
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Zone", id: "All" },
        { type: "Zone", id },
      ],
    }),
    deactivateZone: builder.mutation({
      query: (id) => ({
        ...endpoints.deactivateZone(id),
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Zone", id: "All" },
        { type: "Zone", id },
      ],
    }),
    getAllZonePrices: builder.query({
      query: (data) => ({
        ...endpoints.getAllZonePrices(data),
      }),
      transformResponse: (response) => ({
        data: (response?.data?.data || []).map(normalizeZonePrice),
        page: response?.data?.page || 0,
        total: response?.data?.total || 0,
        limit: response?.data?.limit || 0,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((zonePrice) => ({
                type: "ZonePrice",
                id: zonePrice.id,
              })),
              { type: "ZonePrice", id: "All" },
            ]
          : [{ type: "ZonePrice", id: "All" }],
    }),
    createZonePrice: builder.mutation({
      query: (body) => ({
        ...endpoints.createZonePrice,
        data: body,
      }),
      invalidatesTags: [{ type: "ZonePrice", id: "All" }],
    }),
    updateZonePrice: builder.mutation({
      query: ({ id, body }) => ({
        ...endpoints.updateZonePrice(id),
        data: body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ZonePrice", id: "All" },
        { type: "ZonePrice", id },
      ],
    }),
    deleteZonePrice: builder.mutation({
      query: (id) => ({
        ...endpoints.deleteZonePrice(id),
      }),
      invalidatesTags: [{ type: "ZonePrice", id: "All" }],
    }),
  }),
});

export const {
  useGetAllZonesQuery,
  useGetZoneByIdQuery,
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
  useActivateZoneMutation,
  useDeactivateZoneMutation,
  useGetAllZonePricesQuery,
  useCreateZonePriceMutation,
  useUpdateZonePriceMutation,
  useDeleteZonePriceMutation,
} = ZoneApi;
